/**
 * Utility functions for safe number formatting
 * Prevents "toFixed is not a function" errors by ensuring proper type conversion
 */

/**
 * Safely format a number to a fixed decimal places
 * @param {any} value - The value to format (can be string, number, null, undefined)
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} - Formatted number string
 */
export const safeToFixed = (value, decimals = 2) => {
  const numValue = parseFloat(value);
  if (isNaN(numValue)) return '0'.padEnd(decimals + 2, '.00');
  return numValue.toFixed(decimals);
};

/**
 * Format a percentage value safely
 * @param {any} value - The percentage value
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} - Formatted percentage string with % sign
 */
export const formatPercentage = (value, decimals = 2) => {
  const numValue = parseFloat(value);
  if (isNaN(numValue)) return '0.00%';
  const sign = numValue >= 0 ? '+' : '';
  return `${sign}${numValue.toFixed(decimals)}%`;
};

/**
 * Format currency values safely
 * @param {any} amount - The amount to format
 * @param {number} decimals - Number of decimal places (default: 2)
 * @param {string} currency - Currency symbol (default: '$')
 * @returns {string} - Formatted currency string
 */
export const formatCurrency = (amount, decimals = 2, currency = '$') => {
  const numValue = parseFloat(amount);
  if (isNaN(numValue)) return `${currency}0.00`;
  return `${currency}${numValue.toFixed(decimals)}`;
};

/**
 * Format SOL amounts with appropriate units (SOL, K SOL, M SOL)
 * @param {any} amount - The SOL amount
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} - Formatted SOL string
 */
export const formatSOL = (amount, decimals = 2) => {
  const numAmount = parseFloat(amount);
  if (isNaN(numAmount)) return '0.00 SOL';
  
  if (numAmount >= 1000000) {
    return `${(numAmount / 1000000).toFixed(decimals)}M SOL`;
  }
  if (numAmount >= 1000) {
    return `${(numAmount / 1000).toFixed(decimals)}K SOL`;
  }
  return `${numAmount.toFixed(decimals)} SOL`;
};

/**
 * Format large numbers with K, M, B suffixes
 * @param {any} num - The number to format
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} - Formatted number string
 */
export const formatLargeNumber = (num, decimals = 2) => {
  const numValue = parseFloat(num);
  if (isNaN(numValue)) return '0';
  
  if (numValue >= 1e9) {
    return `${(numValue / 1e9).toFixed(decimals)}B`;
  }
  if (numValue >= 1e6) {
    return `${(numValue / 1e6).toFixed(decimals)}M`;
  }
  if (numValue >= 1e3) {
    return `${(numValue / 1e3).toFixed(decimals)}K`;
  }
  return numValue.toFixed(decimals);
};

/**
 * Format price values with appropriate precision
 * @param {any} price - The price to format
 * @returns {string} - Formatted price string
 */
export const formatPrice = (price) => {
  const numPrice = parseFloat(price);
  if (isNaN(numPrice) || numPrice === 0) return '$0.00';
  
  if (numPrice < 0.000001) return '<$0.000001';
  if (numPrice < 0.01) return `$${numPrice.toFixed(6)}`;
  if (numPrice < 1) return `$${numPrice.toFixed(4)}`;
  if (numPrice < 1000) return `$${numPrice.toFixed(2)}`;
  if (numPrice < 1000000) return `$${(numPrice / 1000).toFixed(1)}K`;
  return `$${(numPrice / 1000000).toFixed(1)}M`;
};

/**
 * Get performance color class based on value
 * @param {any} performance - The performance value
 * @returns {string} - CSS class name
 */
export const getPerformanceColor = (performance) => {
  const numValue = parseFloat(performance);
  if (isNaN(numValue)) return 'neutral';
  if (numValue > 0) return 'positive';
  if (numValue < 0) return 'negative';
  return 'neutral';
};

/**
 * Format token amounts with appropriate precision
 * @param {any} amount - The token amount
 * @param {boolean} isSOL - Whether this is a SOL amount
 * @returns {string} - Formatted amount string
 */
export const formatTokenAmount = (amount, isSOL = false) => {
  const numAmount = parseFloat(amount);
  if (isNaN(numAmount)) return '0.00';
  
  if (numAmount >= 1000) {
    return `${(numAmount / 1000).toFixed(2)}K`;
  }
  if (numAmount >= 1) {
    return numAmount.toFixed(isSOL ? 3 : 2);
  }
  return numAmount.toFixed(6);
};

/**
 * Validate and convert value to number
 * @param {any} value - The value to convert
 * @param {number} defaultValue - Default value if conversion fails
 * @returns {number} - Converted number or default
 */
export const safeNumber = (value, defaultValue = 0) => {
  const numValue = parseFloat(value);
  return isNaN(numValue) ? defaultValue : numValue;
};

export default {
  safeToFixed,
  formatPercentage,
  formatCurrency,
  formatSOL,
  formatLargeNumber,
  formatPrice,
  getPerformanceColor,
  formatTokenAmount,
  safeNumber
};